/**
 * Motore tema: modo (chiaro / scuro / automatico), forma e densità. Il colore è sempre il viola Equal.
 * Sostituisce le formule che erano copiate a mano in App.tsx e nello script inline di index.html.
 *
 * Il colore Equal non scrive nulla su <html>: valgono i valori statici dei fogli di stile. apply() toglie le
 * variabili in linea lasciate dal vecchio selettore di colore e aggiorna `ecrm_theme_cache` (letto dallo script di
 * index.html prima del primo paint).
 *
 * Chiavi di localStorage (prefisso `ecrm_`):
 *   theme_mode   'light' | 'dark' | 'auto'   (già esistente)
 *   radius       'soft' | 'sharp'            (già esistente)
 *   theme_seed / theme_preset                (vecchie: colore scelto, ora ignorate)
 *   theme_cache  JSON {mode, vars}           (nuova, solo per lo script inline)
 *   density      '0' | '-1' | '-2' | '-3'     (nuova; migra da `table_density`, che non viene cancellata)
 *   color_hue / color_sat / color_idx        (vecchie: ignorate, mai cancellate)
 */
import { SCHEME_ROLE_NAMES, generateScheme, hexToTriplet, normalizeHex, tripletToRgbList } from './scheme.ts'

export type ThemeMode = 'light' | 'dark' | 'auto'
export type ShapePref = 'soft' | 'sharp'

/** Seme di default del marchio. */
export const EQUAL_SEED = '#7448D6'

/** Densità M3: 0 (predefinita), -1, -2, -3 (-4dp per passo). Vale per campi, tabelle e liste. */
export type Density = 0 | -1 | -2 | -3
export interface ThemePrefs { mode: ThemeMode; seed: string; shape: ShapePref; density: Density }

/* ── localStorage sempre protetto ── */
function lsGet(k: string, d: string): string {
  try { return localStorage.getItem('ecrm_' + k) || d } catch { return d }
}
function lsSet(k: string, v: string): void {
  try { localStorage.setItem('ecrm_' + k, v) } catch { /* modalità privata o spazio esaurito: si resta in memoria */ }
}

function readMode(): ThemeMode {
  const m = lsGet('theme_mode', 'dark')
  return m === 'light' || m === 'auto' ? m : 'dark'
}

/**
 * Il colore del tema è sempre il viola Equal: la scelta del colore primario (preset e colore personalizzato, colore
 * dinamico Material) è stata tolta col brand kit. Le vecchie chiavi theme_seed/theme_preset/color_* restano ignorate.
 */
export function getThemePrefs(): ThemePrefs {
  const shape: ShapePref = lsGet('radius', 'soft') === 'sharp' ? 'sharp' : 'soft'
  return { mode: readMode(), seed: EQUAL_SEED, shape, density: getDensity() }
}

/* ── Densità ── */
const DENSITY_VALUES: readonly Density[] = [0, -1, -2, -3]
const DENSITY_EVENT = 'ecrm-density'

function parseDensity(v: string): Density | null {
  const n = Number(v)
  return DENSITY_VALUES.find(d => d === n && v.trim() !== '') ?? null
}

/**
 * Densità scelta dall'utente (`ecrm_density`). Se manca, migra la vecchia preferenza delle
 * tabelle (`ecrm_table_density`): comfortable → 0, standard → -2, compact → -3. Predefinita 0.
 * La migrazione non scrive e non cancella nulla: la vecchia chiave resta finché DataTable la legge.
 */
export function getDensity(): Density {
  const saved = parseDensity(lsGet('density', ''))
  if (saved !== null) return saved
  const old = lsGet('table_density', '')
  if (old === 'comfortable') return 0
  if (old === 'standard') return -2
  if (old === 'compact') return -3
  return 0
}

/** Scrive la densità, la applica a <html> (data-density) e avvisa i componenti ('ecrm-density'). */
export function setDensity(d: Density): void {
  lsSet('density', String(d))
  document.documentElement.setAttribute('data-density', String(d))
  window.dispatchEvent(new Event(DENSITY_EVENT))
}

/** Ascolta le modifiche della densità (anche da un'altra scheda). Ritorna la funzione che annulla. */
export function subscribeDensity(cb: () => void): () => void {
  const onStorage = (e: StorageEvent) => { if (e.key === null || e.key === 'ecrm_density') cb() }
  window.addEventListener(DENSITY_EVENT, cb)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(DENSITY_EVENT, cb)
    window.removeEventListener('storage', onStorage)
  }
}

/* ── Applicazione ── */
const BS_PRIMARY_VARS = ['--bs-primary-rgb', '--bs-link-color-rgb'] as const
/** Colore pieno del ramo per la navigazione (voce attiva, bagliore) e il suo testo; primario della navigazione scura. */
const ACCENT_EXTRA_VARS = ['--md-accent-fill', '--md-on-accent-fill', '--md-nav-primary'] as const
const MANAGED_VARS: readonly string[] = [
  ...SCHEME_ROLE_NAMES.map(n => '--md-' + n),
  ...BS_PRIMARY_VARS,
  ...ACCENT_EXTRA_VARS,
]

export function resolvedMode(): 'light' | 'dark' {
  const mode = readMode()
  if (mode === 'auto') {
    try { return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light' } catch { return 'dark' }
  }
  return mode
}

function varsFor(seed: string, mode: 'light' | 'dark'): Record<string, string> {
  if (seed.toUpperCase() === EQUAL_SEED) return {}
  const roles = generateScheme(seed)[mode]
  const vars: Record<string, string> = {}
  for (const [name, triplet] of Object.entries(roles)) vars['--md-' + name] = triplet
  const rgb = tripletToRgbList(roles['primary'] ?? hexToTriplet(seed))
  for (const name of BS_PRIMARY_VARS) vars[name] = rgb
  return vars
}

/* ── ADR-019 (Equal Point): gli accenti seguono il ramo ── */
// Il colore del ramo scelto cambia solo gli accenti (primario, contenitori, voce attiva e bagliore della navigazione):
// sfondi, superfici e testi restano quelli Equal. La Generale torna al viola. Si ricorda a parte (`ecrm_branch_accent`) e
// finisce in theme_cache, così la ricarica parte già col colore del ramo.
let branchAccent: string | null = normalizeHex(lsGet('branch_accent', '')) ?? null

/** Colore del ramo (#RRGGBB) per gli accenti; null torna al viola Equal. */
export function setBranchAccent(hex: string | null): void {
  const next = hex ? normalizeHex(hex) : null
  if (next === branchAccent) return
  branchAccent = next
  lsSet('branch_accent', next ?? '')
  apply()
}

/** Ruoli d'accento presi dallo schema del ramo; tutti gli altri restano quelli statici di brand.css. */
const ACCENT_ROLES = [
  'primary', 'on-primary', 'primary-container', 'on-primary-container',
  'secondary', 'on-secondary', 'secondary-container', 'on-secondary-container', 'inverse-primary',
] as const

function luminance(hex: string): number {
  const c = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
}

export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p)
  return (x + 0.05) / (y + 0.05)
}

/** Testo nero o bianco sopra un colore pieno, quello che si legge meglio. */
function onColor(hex: string): string {
  return contrast(hex, '#121212') >= contrast(hex, '#FFFFFF') ? hexToTriplet('#121212') : hexToTriplet('#FFFFFF')
}

/**
 * Accenti del ramo. Il primario è il colore del marchio quando si legge sullo sfondo (carta nel chiaro, nero nello scuro),
 * altrimenti il tono più leggibile dello stesso colore; il pieno della navigazione è il colore del marchio se si distingue
 * dal nero della barra (il verde scuro di Studio no: usa il suo tono acceso).
 */
export function accentVarsFor(hex: string, mode: 'light' | 'dark'): Record<string, string> {
  const scheme = generateScheme(hex)
  const roles = scheme[mode]
  const vars: Record<string, string> = {}
  for (const name of ACCENT_ROLES) if (roles[name]) vars['--md-' + name] = roles[name]
  const background = mode === 'light' ? '#F5F1EC' : '#121212'
  if (contrast(hex, background) >= (mode === 'light' ? 3 : 4.5)) {
    vars['--md-primary'] = hexToTriplet(hex)
    vars['--md-on-primary'] = onColor(hex)
  }
  const fill = contrast(hex, '#0E0E0E') >= 2 ? hex : null
  vars['--md-accent-fill'] = fill ? hexToTriplet(fill) : scheme.dark['primary-container']
  vars['--md-on-accent-fill'] = fill ? onColor(fill) : scheme.dark['on-primary-container']
  vars['--md-nav-primary'] = scheme.dark['primary']
  const rgb = tripletToRgbList(vars['--md-primary'])
  for (const name of BS_PRIMARY_VARS) vars[name] = rgb
  return vars
}

function apply(): void {
  const root = document.documentElement
  const prefs = getThemePrefs()
  const mode = resolvedMode()
  root.setAttribute('data-theme', mode)
  root.setAttribute('data-shape', prefs.shape)
  root.setAttribute('data-density', String(prefs.density))
  for (const name of MANAGED_VARS) root.style.removeProperty(name)
  const vars = { ...varsFor(prefs.seed, mode), ...(branchAccent ? accentVarsFor(branchAccent, mode) : {}) }
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value)
  lsSet('theme_cache', JSON.stringify({ mode, vars }))
}

/** Applica il tema salvato e segue prefers-color-scheme (modo 'auto') e le altre schede. */
export function initTheme(): () => void {
  apply()
  let mq: MediaQueryList | null = null
  try { mq = window.matchMedia('(prefers-color-scheme: dark)') } catch { mq = null }
  const onScheme = () => { if (readMode() === 'auto') apply() }
  const onStorage = (e: StorageEvent) => {
    // theme_cache è un dato derivato: se lo si rileggesse, due schede con colori diversi se lo riscriverebbero a vicenda.
    if (e.key === 'ecrm_theme_cache') return
    if (e.key === null || e.key.startsWith('ecrm_theme') || e.key === 'ecrm_radius' || e.key === 'ecrm_density' || e.key === 'ecrm_table_density') apply()
  }
  mq?.addEventListener('change', onScheme)
  window.addEventListener('storage', onStorage)
  return () => {
    mq?.removeEventListener('change', onScheme)
    window.removeEventListener('storage', onStorage)
  }
}

export function setThemeMode(m: ThemeMode): void {
  lsSet('theme_mode', m)
  apply()
}

export function setShape(s: ShapePref): void {
  lsSet('radius', s)
  apply()
}
